import { useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '../ui/Badge';
import { NumberInput } from '../ui/NumberInput';
import { btnSecondary } from '../ui/styles';
import { money, toCents } from '../../core/format/currency';
import { patchStockItem } from '../adminApi';
import { errorMessage } from '../../core/errors';
import { locationLabel } from '../../core/rules/stockMovements';
import { stockLocations, type StockItem, type StockMovementType } from '../../core/types/stock';
import { StockActionForm } from './StockActionForm';
import { StockHistory } from './StockHistory';
type Props = {
  item: StockItem;
  open: boolean;
  onToggle: () => void;
  onChange: (item: StockItem) => void;
};

/** Knoppen in volgorde van hoe vaak ze nodig zijn. Plek en richting kies je in het formulier. */
const actions: {
  type: StockMovementType;
  label: string;
}[] = [{
  type: 'overboeken',
  label: 'Overboeken'
}, {
  type: 'verbruik',
  label: 'In gebruik genomen'
}, {
  type: 'inkoop',
  label: 'Inkoop ontvangen'
}, {
  type: 'afschrijven',
  label: 'Afschrijven'
}, {
  type: 'telling',
  label: 'Tellen'
}];
const levelText = (v: number | null) => v === null ? '—' : String(v);

/** Eén artikel: standen per plek, verkopen van de laatste 30 dagen, en open: acties, inkoopprijs en historie. */
export function StockRow({
  item,
  open,
  onToggle,
  onChange
}: Props) {
  const [action, setAction] = useState<StockMovementType | null>(null);
  const [version, setVersion] = useState(0);
  const panelId = `voorraad-${item.id}`;
  const done = (next: StockItem) => {
    setAction(null);
    setVersion(v => v + 1);
    onChange(next);
  };
  return <li data-id="element-5021" data-mp="rgt2og2x9qxu">
      <button type="button" onClick={onToggle} aria-expanded={open} aria-controls={panelId} className="grid w-full grid-cols-[minmax(0,1fr)_auto_auto_16px] items-center gap-x-4 px-4 py-3 text-left transition-colors duration-150 ease-soft hover:bg-shell sm:grid-cols-[minmax(0,1fr)_72px_72px_120px_16px] sm:px-5" data-id="element-5022" data-mp="rgjcardythrm">
        <span className="min-w-0" data-id="element-5023" data-mp="2l7qdsk9j3g48">
          <span className="block truncate text-[14px] text-ink" data-id="element-5024" data-mp="2g8n8cypmz7vq">{item.name}</span>
          <span className="block truncate text-[12px] text-muted" data-id="element-5025" data-mp="2garocf0t7xcd">
            {item.brand ?? (item.kind === 'verbruik' ? 'Verbruik' : 'Product')}
            {item.kind === 'verbruik' && item.brand && ' · verbruik'}
          </span>
        </span>
        {stockLocations.map(l => {
        const v = item.levels[l];
        return <span key={l} className="text-right" data-id="element-5026" data-mp="2z8149m6xesov">
              <span className="block text-[11px] text-muted sm:hidden" data-id="element-5027" data-mp="2z5f0lxn5oxbx">{locationLabel[l]}</span>
              <span className={`text-[14px] tabular-nums ${v !== null && v <= 0 ? 'text-clay' : 'text-ink'}`} data-id="element-5028" data-mp="3dy8ddbb1mzcw">{levelText(v)}</span>
            </span>;
      })}
        <span className="hidden text-right text-[12px] tabular-nums text-muted sm:block" data-id="element-5029" data-mp="3mxn6kiilho13">
          {item.kind === 'retail' ? `${item.sold30.webshop} web · ${item.sold30.salon} salon` : '—'}
        </span>
        <ChevronDownIcon className={`h-4 w-4 text-muted transition-transform duration-150 ease-soft ${open ? 'rotate-180' : ''}`} strokeWidth={1.5} aria-hidden="true" data-id="element-5030" data-mp="5eo8tcv2sh0w" />
      </button>

      {open && <div id={panelId} className="space-y-5 border-t border-line bg-shell px-4 py-4 sm:px-5" data-id="element-5031" data-mp="3sicnvoq3ir2y">
          <dl className="grid grid-cols-2 gap-4 text-[13px] sm:grid-cols-4" data-id="element-5032" data-mp="1b02exi6jrh8">
            {stockLocations.map(l => <div key={l} data-id="element-5033" data-mp="3iqyi4z7jwv1d">
                <dt className="text-muted" data-id="element-5034" data-mp="3njhvbv2bto5n">{locationLabel[l]}</dt>
                <dd className="text-[16px] tabular-nums text-ink" data-id="element-5035" data-mp="3nlukas6i4ys7">
                  {levelText(item.levels[l])}
                  {item.levels[l] !== null && (item.levels[l] ?? 0) < 0 && <span className="ml-2 text-[12px] text-clay" data-id="element-5036" data-mp="3durfpged61o6">tel opnieuw</span>}
                </dd>
              </div>)}
            {item.kind === 'retail' && <div className="col-span-2" data-id="element-5037" data-mp="34tjm3bmslp0f">
                <dt className="text-muted" data-id="element-5038" data-mp="34vcsqqxeo98f">Verkocht, laatste 30 dagen</dt>
                <dd className="text-ink" data-id="element-5039" data-mp="b00i78as37jp">
                  <span className="tabular-nums" data-id="element-5040" data-mp="6zox5uei757p">{item.sold30.webshop}</span> via de webshop · <span className="tabular-nums" data-id="element-5041" data-mp="70i4awhdjwqd">{item.sold30.salon}</span> in de salon
                </dd>
              </div>}
          </dl>
          {item.kind === 'retail' && <p className="text-[12px] text-muted" data-id="element-5042" data-mp="3tf19c2kglzk6">De webshop verkoopt alleen uit de webshopvoorraad: staat die op 0, dan is het product online uitverkocht, ook als de salon er nog heeft.</p>}

          {/* key: een nieuwe kostprijs van de server zet het veld opnieuw, zodat Opslaan nooit een oude prijs terugzet. */}
          <CostPrice key={item.costPriceCents ?? 'leeg'} item={item} onChange={onChange} data-id="element-5043" data-mp="3agf7glh4c4f7" />

          {action ? <StockActionForm item={item} type={action} onDone={done} onCancel={() => setAction(null)} data-id="element-5044" data-mp="1dnvlw5h4chr8" /> : <div className="flex flex-wrap gap-2" role="group" aria-label={`Acties voor ${item.name}`} data-id="element-5045" data-mp="18osl4x868xp2">
              {actions.map(a => <button key={a.type} type="button" onClick={() => setAction(a.type)} className={btnSecondary} data-id="element-5046" data-mp="1g6h4ne5b61ml">
                  {a.label}
                </button>)}
            </div>}

          <section aria-label="Historie" data-id="element-5047" data-mp="1q0qvusiiktvq">
            <h3 className="mb-1 text-[13px] font-medium text-ink" data-id="element-5048" data-mp="1uxr336r3o2of">Historie</h3>
            <StockHistory key={version} itemId={item.id} data-id="element-5049" data-mp="x9z3k64kkpdo" />
          </section>
        </div>}
    </li>;
}
function CostPrice({
  item,
  onChange
}: {
  item: StockItem;
  onChange: (item: StockItem) => void;
}) {
  const saved = item.costPriceCents === undefined ? undefined : item.costPriceCents / 100;
  const [value, setValue] = useState(saved);
  const [busy, setBusy] = useState(false);
  const changed = value !== saved;
  const save = async () => {
    setBusy(true);
    try {
      const next = await patchStockItem(item.id, {
        costPriceCents: value === undefined ? null : toCents(value)
      });
      onChange(next);
      toast.success('Inkoopprijs opgeslagen');
    } catch (err) {
      toast.error('Opslaan lukte niet', {
        description: errorMessage(err) ?? 'Probeer het zo nog eens.'
      });
    } finally {
      setBusy(false);
    }
  };
  return <div className="flex flex-wrap items-end gap-2" data-id="element-5050" data-mp="wk21hbvnngbz">
      <label className="block" data-id="element-5051" data-mp="rlqst4h3jbvc">
        <span className="mb-1 block text-[12px] text-muted" data-id="element-5052" data-mp="rmses3znhiru">Inkoopprijs per stuk (€, excl. btw)</span>
        <NumberInput decimals allowEmpty value={value} onChange={setValue} placeholder="Onbekend" className="w-32" data-id="element-5053" data-mp="3wpptwr18sxg" />
      </label>
      {changed && <button type="button" onClick={() => void save()} disabled={busy} className={btnSecondary} data-id="element-5054" data-mp="dlpsbq4qu6ge">
          {busy ? 'Bezig…' : 'Opslaan'}
        </button>}
      {!changed && saved !== undefined && <Badge tone="neutral" data-id="element-5055" data-mp="3hajacvab4rsu">{money(item.costPriceCents ?? 0)}</Badge>}
    </div>;
}